"use client";

import { memo } from "react";
import { useViewerLanguage } from "@/lib/i18n";
import { useChartProjection } from "../projection";
import { TooltipBox } from "../tooltip/tooltip-box";
import {
  type HeatmapTooltipData,
  useHeatmap,
  useHeatmapInteraction,
} from "./heatmap-context";
import {
  formatHeatmapTooltipDate,
  formatHeatmapTooltipWeekday,
} from "./heatmap-utils";
import { useDelayedTooltipData } from "./use-delayed-tooltip-data";

export interface HeatmapTooltipProps {
  /** Custom contribution line (bottom section). Default: `N contribution(s)`. */
  formatLabel?: (count: number, date: Date) => string;
  /**
   * Custom heading. Default: the bin date. Categorical grids pass their column
   * and row names here, where the bin date is only a layout placeholder.
   */
  formatTitle?: (data: HeatmapTooltipData) => string;
  /** Custom sub-heading. Default: the bin weekday. Return `null` to omit it. */
  formatSubtitle?: (data: HeatmapTooltipData) => string | null;
  /** Custom class name */
  className?: string;
  /** Inline styles for the tooltip panel (background, blur, etc.). */
  panelStyle?: React.CSSProperties;
  /**
   * Tooltip panel background color (CSS variable or color value).
   * Default: `var(--chart-tooltip-background)`.
   */
  backgroundColor?: string;
  /**
   * Delay before showing the tooltip on first hover (ms).
   * Moving between cells updates immediately once visible.
   */
  showDelay?: number;
  /**
   * Grace period before hiding when the pointer leaves a cell (ms).
   * Helps avoid flicker when moving quickly between adjacent cells.
   */
  hideDelay?: number;
  /**
   * When true, the tooltip appears and disappears instantly with no motion.
   */
  instant?: boolean;
}

export const HeatmapTooltip = memo(function HeatmapTooltip({
  formatLabel,
  formatTitle,
  formatSubtitle,
  className = "",
  panelStyle,
  backgroundColor,
  showDelay = 0,
  hideDelay = 120,
  instant = false,
}: HeatmapTooltipProps) {
  const { containerRef, width, height } = useHeatmap();
  const { tooltipData } = useHeatmapInteraction();
  const displayData = useDelayedTooltipData(tooltipData, showDelay, hideDelay);
  const isometric = useChartProjection()?.isometric ?? false;
  const { t, intlLocale } = useViewerLanguage();

  if (!displayData) {
    return null;
  }

  const { count, date } = displayData;
  const title = formatTitle
    ? formatTitle(displayData)
    : formatHeatmapTooltipDate(date, intlLocale);
  const subtitle = formatSubtitle
    ? formatSubtitle(displayData)
    : formatHeatmapTooltipWeekday(date, intlLocale);
  const label = formatLabel
    ? formatLabel(count, date)
    : t("{count} contributions", { count });

  return (
    <TooltipBox
      animate={false}
      backgroundColor={backgroundColor}
      className={className}
      containerHeight={height}
      containerRef={containerRef}
      containerWidth={width}
      entrance={!instant}
      panelStyle={panelStyle}
      placement={isometric ? "above" : undefined}
      visible
      x={displayData.x}
      y={displayData.y}
    >
      <div className="overflow-hidden">
        <div className="px-3 py-2.5 text-left">
          <div className="font-medium text-chart-tooltip-foreground text-xs">
            {title}
          </div>
          {subtitle ? (
            <div className="mt-0.5 text-chart-tooltip-muted text-xs">
              {subtitle}
            </div>
          ) : null}
          <div className="my-2 border-chart-tooltip-muted/30 border-t" />
          <div className="text-chart-tooltip-foreground text-sm">
            {label}
          </div>
        </div>
      </div>
    </TooltipBox>
  );
});

HeatmapTooltip.displayName = "HeatmapTooltip";

export default HeatmapTooltip;
